import { useMemo } from 'react';
import { useChatRooms } from './useChatRooms';

const normalizeId = (value) => (value === undefined || value === null ? '' : String(value));

const idsMatch = (left, right) => {
  const normalizedLeft = normalizeId(left);
  const normalizedRight = normalizeId(right);
  return normalizedLeft !== '' && normalizedRight !== '' && normalizedLeft === normalizedRight;
};

const roomMatchesBooking = (room, bookingId) => {
  if (!room || !normalizeId(bookingId)) return false;

  if (idsMatch(room.id, bookingId) || idsMatch(room.linkedBookingID, bookingId)) {
    return true;
  }

  return Array.isArray(room.linkedBookingIDs) && room.linkedBookingIDs.some((linkedId) => idsMatch(linkedId, bookingId));
};

/**
 * Helper function to format relative time
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted relative time (e.g., "2h", "3d", "1w")
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return null;
  
  const now = new Date();
  const messageDate = new Date(dateString);
  const diffInMs = now - messageDate;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  const diffInWeeks = Math.floor(diffInDays / 7);

  if (diffInMinutes < 60) {
    return diffInMinutes <= 0 ? "jetzt" : `${diffInMinutes}m`;
  } else if (diffInHours < 24) {
    return `${diffInHours}h`;
  } else if (diffInDays < 7) {
    return `${diffInDays}d`;
  } else if (diffInWeeks < 4) {
    return `${diffInWeeks}w`;
  } else {
    // For older messages, show the date
    return messageDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
  }
};

/**
 * Custom hook to get latest message content between two users for a specific studio
 * Uses the new V2 messaging system via useChatRooms
 * @param {string} studioId - Studio ID
 * @param {string} userId1 - First user ID (studio owner/host)
 * @param {string} userId2 - Second user ID (booking customer/guest)
 * @param {Object} bookingContext - Optional booking-specific matching data
 * @returns {Object} Latest message content and time
 */
export const useFetchLatestMessage = (studioId, userId1, userId2, bookingContext = {}) => {
  // Use the new V2 chat rooms hook which already has all the data we need
  const { chatRooms, loading, error } = useChatRooms(userId1);
  const { chatRoomId, bookingId } = bookingContext || {};
  const hasBookingContext = !!normalizeId(chatRoomId) || !!normalizeId(bookingId);

  const { latestMessageContent, latestMessageTime } = useMemo(() => {
    // Skip if required parameters are missing
    if (!studioId || !userId1 || !userId2 || !chatRooms || chatRooms.length === 0) {
      return { latestMessageContent: null, latestMessageTime: null };
    }

    let matchingChatRoom = null;

    if (chatRoomId) {
      matchingChatRoom = chatRooms.find((room) => idsMatch(room.id, chatRoomId));
    }

    if (!matchingChatRoom && bookingId) {
      matchingChatRoom = chatRooms.find((room) => roomMatchesBooking(room, bookingId));
    }

    if (!matchingChatRoom && !hasBookingContext) {
      matchingChatRoom = chatRooms.find(room => {
        const participantIDs = Array.isArray(room.participantIDs) ? room.participantIDs : [];
        const matchesStudio = room.studioID === studioId;
        const matchesPartner =
          idsMatch(room.chatPartner?.id, userId2) ||
          idsMatch(room.hostID, userId2) ||
          idsMatch(room.guestID, userId2) ||
          participantIDs.some((participantId) => idsMatch(participantId, userId2));
        return matchesStudio && matchesPartner;
      });
    }

    if (!matchingChatRoom) {
      return { latestMessageContent: null, latestMessageTime: null };
    }

    // Get the last message from the chat room
    // The new system stores lastMessageContent and lastMessageAt directly on the chat room
    const lastMessageContent = matchingChatRoom.lastMessage?.content || matchingChatRoom.lastMessageContent || null;
    const lastMessageAt = matchingChatRoom.lastMessage?.createdAt || matchingChatRoom.lastMessageAt || null;

    return {
      latestMessageContent: lastMessageContent,
      latestMessageTime: lastMessageAt ? formatRelativeTime(lastMessageAt) : null
    };
  }, [chatRooms, studioId, userId1, userId2, chatRoomId, bookingId, hasBookingContext]);

  return {
    latestMessageContent,
    latestMessageTime,
    loading,
    error
  };
};
